/* eslint-disable import/no-duplicates */
import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  Fragment,
} from 'react';
import { useHistory, Link } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import {
  addMonths,
  eachMonthOfInterval,
  endOfMonth,
  format,
  parseISO,
  startOfMonth,
} from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../../services/api';

import { STATUS_AGENDA, TIPO_USUARIO } from '../../../utils/funcoes';

import { useToast } from '../../../hooks/toast';
import { useAuth } from '../../../hooks/auth';

import Input from '../../../components/Input';
import Loading from '../../../components/Loading';

import getValidationErrors from '../../../utils/getValidationErrors';

import { arrayGroupBy } from '../../../utils/funcoes';

import getResponseErrors from '../../../utils/getResponseErrors';
import PageInfo from '../../../components/PageInfo';
import HeaderRelatorio from '../HeaderRelatorio';

interface IRetornoListaAgendamentos {
  UsuCodigo: number;
  UsuNome: string;
  total: number;
  AgeStatus: number;
  AgePlano: boolean;
  mes: string;
}

interface IAgendamentosMedico {
  mes: string;
  totalAgendado: number;
  totalAtendido: number;
  totalNaoAtendido: number;
}

interface IRelatorio {
  UsuCodigo: number;
  UsuNome: string;
  valores: IAgendamentosMedico[];
}

interface IFormBusca {
  dataInicio: Date;
  dataFim: Date;
}

interface IDataExibe {
  dataHora: string;
  dataDiaSemana: string;
  dataInicio: string;
  dataFim: string;
}

const MedicosAgendamentos: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user } = useAuth();

  const formRefBusca = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [dadosRelatorio, setDadosRelatorio] = useState<IRelatorio[]>();

  const initialData = {
    dataInicio: format(addMonths(startOfMonth(new Date()), -6), 'yyyy-MM'),
    dataFim: format(endOfMonth(new Date()), 'yyyy-MM'),
  };
  const [dataExibe, setDataExibe] = useState<IDataExibe>();

  const informacoesPagina = `<b>Descrição:</b> Exibe total de agendamentos do período selecionado por o médico.<br /> <b>Não Atendindos</b> entram todos os status que não foram atendidos (Aguardando Confirmação, Cancelados, Faltas, Estorno)`;

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.SUPERVISOR) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  const handleSubmitBusca = useCallback(
    async (dados: IFormBusca) => {
      setLoading(true);
      try {
        formRefBusca.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dataInicioSelecionada = parseISO(dados.dataInicio.toString());
        const dataFimSelecionada = endOfMonth(
          parseISO(dados.dataFim.toString()),
        );

        const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

        setDataExibe({
          dataDiaSemana: `${format(
            dataInicioSelecionada,
            'dd/MM/yyyy',
          )} até ${format(dataFimSelecionada, 'dd/MM/yyyy')}`,
          dataHora: format(new Date(), 'dd/MM/yyyy HH:mm:ss'),
          dataInicio: dataInicioFormatada,
          dataFim: dataFimFormatada,
        });

        const response = await api.get<IRetornoListaAgendamentos[]>(
          `/relatorios/medicolistaagendamentos?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}`,
        );

        const meses = eachMonthOfInterval({
          start: dataInicioSelecionada,
          end: dataFimSelecionada,
        }).map(data => format(data, 'MM/yyyy'));

        const agrupadoPorMedico = arrayGroupBy(
          response.data,
          rel => rel.UsuCodigo,
        );

        const relatorioFormatado = agrupadoPorMedico.map(medico => {
          const { UsuCodigo, UsuNome } = medico[0];
          const dadosAgendamento: IAgendamentosMedico[] = [];

          meses.forEach(mes => {
            const mesFiltrado = medico.filter(rel => rel.mes === mes);
            if (mesFiltrado) {
              const totalAtendido = mesFiltrado
                .filter(
                  rel =>
                    rel.AgeStatus === STATUS_AGENDA.ATENDIDO ||
                    rel.AgeStatus === STATUS_AGENDA.AGUARDANDO_ATENDIMENTO,
                )
                .reduce((tot, item) => {
                  return tot + Number(item.total || 0);
                }, 0);

              const totalNaoAtendido = mesFiltrado
                .filter(
                  rel =>
                    rel.AgeStatus !== STATUS_AGENDA.ATENDIDO &&
                    rel.AgeStatus !== STATUS_AGENDA.AGUARDANDO_ATENDIMENTO,
                )
                .reduce((tot, item) => {
                  return tot + Number(item.total || 0);
                }, 0);

              const totalAgendado = mesFiltrado.reduce((tot, item) => {
                return tot + Number(item.total || 0);
              }, 0);

              dadosAgendamento.push({
                mes,
                totalAgendado,
                totalAtendido,
                totalNaoAtendido,
              });
            }
            if (!mesFiltrado) {
              dadosAgendamento.push({
                mes,
                totalAgendado: 0,
                totalAtendido: 0,
                totalNaoAtendido: 0,
              });
            }
          });
          return {
            UsuCodigo,
            UsuNome,
            valores: dadosAgendamento,
          };
        });

        setDadosRelatorio(relatorioFormatado);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBusca.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      <>
        <div className="d-print-none bg-white border p-2 mb-2">
          <div className="p-2 mb-2 d-flex justify-content-between">
            <h2 id="topoBusca">Medicos Agendamentos</h2>
            <PageInfo
              descricao={informacoesPagina}
              permissao={TIPO_USUARIO.SUPERVISOR}
            />
          </div>
          {user && (
            <Form
              ref={formRefBusca}
              initialData={initialData}
              onSubmit={handleSubmitBusca}
            >
              <Row>
                <Col md={2}>
                  <Input name="dataInicio" label="De" type="month" required />
                </Col>
                <Col md={2}>
                  <Input name="dataFim" label="Até" type="month" required />
                </Col>
                <Col md={2}>
                  <br />
                  <Button type="submit" size="sm" variant="outline-secondary">
                    BUSCAR
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </div>

        <HeaderRelatorio
          dataImpressao={dataExibe?.dataHora}
          titulo={`Agendamentos ${dataExibe?.dataDiaSemana || ''}`}
        />

        <div className="bg-white border p-2">
          {dadosRelatorio && (
            <>
              <table
                className="table table-hover table-sm table-font-small table-bordered"
                style={{ borderWidth: '0px' }}
              >
                <thead>
                  <tr className="text-center text-white bg-verde">
                    <th rowSpan={2}>Medico</th>
                    {dadosRelatorio &&
                      dadosRelatorio[0].valores.map(rel => (
                        <th key={`rel1-${rel.mes}`} colSpan={3}>
                          {rel.mes}
                        </th>
                      ))}
                  </tr>

                  <tr className="text-center text-white bg-verde">
                    {dadosRelatorio &&
                      dadosRelatorio[0].valores.map(rel => (
                        <Fragment key={`head-${rel.mes}`}>
                          <td>Agendado</td>
                          <td>Atendido</td>
                          <td>Não Atendido</td>
                        </Fragment>
                      ))}
                  </tr>
                </thead>

                <tbody>
                  {dadosRelatorio &&
                    dadosRelatorio.map(rel => (
                      <tr
                        key={`rel-${rel.UsuCodigo}`}
                        data-id={rel.UsuCodigo}
                        style={{ fontSize: '.7rem', height: '30px' }}
                      >
                        <td>
                          <Link
                            to={`/dashboardmedico?data=${dataExibe?.dataInicio}&dataFim=${dataExibe?.dataFim}&usuario=${rel.UsuCodigo}`}
                            target="_blank"
                          >
                            {rel.UsuNome}
                          </Link>
                        </td>
                        {rel.valores.map(valores => (
                          <>
                            <td>{valores.totalAgendado}</td>
                            <td>{valores.totalAtendido}</td>
                            <td>{valores.totalNaoAtendido}</td>
                          </>
                        ))}
                      </tr>
                    ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </>
    </Container>
  );
};

export default MedicosAgendamentos;
